import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { ArrowRight, Building2, Sparkles, Filter, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const caseStudiesData = [
  {
    id: 'engenoil',
    title: 'SAP S/4HANA Migration for EngenOil',
    client: 'EngenOil',
    industry: 'Petroleum',
    category: 'SAP S/4HANA Migration',
    image: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=800&q=80',
    description: 'EngenOil, a major player in the petroleum industry, faced increasing challenges with its legacy ERP system. The company was experiencing difficulties with real-time data processing, slow response times, and inefficiencies in managing critical functions like finance, supply chain, and asset management.',
    challenge: 'As the business grew and the industry became more dynamic, EngenOil recognized the need for a modern ERP system to streamline operations, improve efficiency, and enable real-time decision-making.',
    solution: 'UZVI Services delivered a comprehensive SAP S/4HANA migration, transforming their entire business operations with real-time analytics and improved process efficiency.',
    results: [
      '40% improvement in real-time data processing',
      '30% reduction in operational costs',
      'Streamlined finance and supply chain operations',
      'Enhanced asset management capabilities'
    ]
  },
  {
    id: 'sanofi',
    title: 'SAP S/4HANA Migration at Sanofi',
    client: 'Sanofi',
    industry: 'Pharmaceutical',
    category: 'SAP S/4HANA Migration',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&q=80',
    description: 'Sanofi, a leading global pharmaceutical company headquartered in Paris, is renowned for its extensive product portfolio across prescription medicines, consumer healthcare, and vaccines.',
    challenge: 'To continue driving innovation and improve operational efficiency, Sanofi embarked on a large-scale digital transformation journey, selecting SAP S/4HANA as the core technology platform to modernize and integrate its global business operations.',
    solution: 'UZVI Services partnered with Sanofi to execute a seamless SAP S/4HANA implementation across their global operations, ensuring minimal disruption to ongoing business activities.',
    results: [
      'Successfully integrated global business operations',
      'Improved supply chain visibility across 100+ countries',
      'Enhanced compliance and regulatory reporting',
      'Reduced time-to-market for new products'
    ]
  },
  {
    id: 'ascensus',
    title: 'SAP S/4 HANA Migration for Ascensus',
    client: 'Ascensus',
    industry: 'Financial Services',
    category: 'SAP S/4HANA Migration',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
    description: 'Ascensus, a prominent American financial services provider, specializes in retirement, college savings, and health savings solutions. Founded in 1980 and headquartered in Dresher, Pennsylvania, Ascensus has grown to manage over $400 billion in assets.',
    challenge: 'As a company with a complex, data-intensive operational structure, Ascensus sought a robust solution to streamline processes, improve data accuracy, and drive scalable growth.',
    solution: 'UZVI Services implemented a tailored SAP S/4HANA solution that addressed the unique needs of financial services, with emphasis on data security, compliance, and scalability.',
    results: [
      'Managing over $400 billion in assets efficiently',
      'Improved data accuracy and reporting',
      'Enhanced scalability for future growth',
      'Strengthened compliance framework'
    ]
  },
  {
    id: 'retail-chain',
    title: 'Cloud Migration for Global Retail Chain',
    client: 'Global Retail Corp',
    industry: 'Retail',
    category: 'Cloud Solutions',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80',
    description: 'A leading global retail chain with operations in 25 countries needed to modernize their infrastructure to support rapid e-commerce growth.',
    challenge: 'The client faced challenges with scalability during peak seasons, high infrastructure costs, and inability to quickly deploy new features to compete in the fast-paced retail market.',
    solution: 'UZVI Services designed and executed a comprehensive Azure cloud migration strategy, implementing microservices architecture and automated scaling solutions.',
    results: [
      '60% reduction in infrastructure costs',
      '99.99% uptime during peak seasons',
      '3x faster feature deployment',
      'Seamless handling of 10x traffic spikes'
    ]
  },
  {
    id: 'healthcare-app',
    title: 'Healthcare Mobile App Development',
    client: 'MedConnect Health',
    industry: 'Healthcare',
    category: 'Mobile Development',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&q=80',
    description: 'MedConnect Health required a HIPAA-compliant mobile application to connect patients with healthcare providers and manage medical records securely.',
    challenge: 'Building a secure, user-friendly mobile app that complies with strict healthcare regulations while providing seamless experience for both patients and doctors.',
    solution: 'UZVI Services developed a cross-platform mobile application using React Native with end-to-end encryption and comprehensive security measures.',
    results: [
      '100% HIPAA compliance achieved',
      '50,000+ active users in first 6 months',
      '4.8 star rating on app stores',
      '40% reduction in appointment no-shows'
    ]
  },
  {
    id: 'ecommerce-platform',
    title: 'E-Commerce Platform Development',
    client: 'TechMart Online',
    industry: 'E-Commerce',
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    description: 'TechMart Online needed a modern, scalable e-commerce platform to support their growing electronics retail business.',
    challenge: 'The existing platform could not handle increasing traffic, had poor mobile experience, and lacked modern features like real-time inventory management.',
    solution: 'UZVI Services built a custom e-commerce solution using Next.js and Node.js with integrated inventory management, payment processing, and analytics.',
    results: [
      '200% increase in online sales',
      '45% improvement in mobile conversion',
      'Real-time inventory across 50+ warehouses',
      '2 second average page load time'
    ]
  }
];

const categories = ['All', 'SAP S/4HANA Migration', 'Cloud Solutions', 'Mobile Development', 'Web Development'];

const CaseStudiesPage = () => {
  const pageRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [caseStudies, setCaseStudies] = useState(caseStudiesData);

  useEffect(() => {
    // Try to fetch from API, fallback to static data
    fetch(`${API_URL}/api/case-studies`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setCaseStudies(data);
        }
      })
      .catch(() => {
        // Use static data
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [caseStudies, selectedCategory]);

  const filteredStudies = selectedCategory === 'All' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-6">
                <Building2 className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-sm font-medium text-gray-700">Success Stories</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-testid="case-studies-title">
                Our
                <span className="bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x"> Case Studies</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Discover how we've helped businesses across industries transform their operations and achieve remarkable results.
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8" data-testid="case-studies-grid">
              {filteredStudies.map((study, index) => (
                <Link
                  key={study.id}
                  to={`/case-studies/${study.id}`}
                  className="reveal-scale group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`case-study-card-${study.id}`}
                >
                  <div className="bento-card h-full overflow-hidden hover:border-[#ff6b35]/30">
                    <div className="relative h-56 -mx-8 -mt-8 mb-6 overflow-hidden">
                      <img 
                        src={study.image} 
                        alt={study.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[#ff6b35] text-white text-xs font-semibold rounded-full">
                          {study.category}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-white/80 text-sm">{study.industry}</span>
                        <h3 className="text-xl font-bold text-white mt-1 group-hover:text-[#ff6b35] transition-colors">
                          {study.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {study.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-500">Client: {study.client}</span>
                      <span className="inline-flex items-center gap-2 text-[#ff6b35] font-semibold text-sm group-hover:gap-3 transition-all">
                        Read Case
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredStudies.length === 0 && (
              <div className="text-center py-20">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No case studies found</h3>
                <p className="text-gray-500">Try selecting a different category</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-[#ff6b35] mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 reveal">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-gray-400 text-lg mb-10 reveal">
              Let's discuss how we can help transform your business.
            </p>
            <Link 
              to="/contact"
              className="reveal inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default CaseStudiesPage;
